import React, { FC, useMemo, useEffect } from "react";
import { Divider, Select, Alert } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import {
  useGetAllCompaniesQuery,
  useGetAllOrdersQuery,
  useGetAllPackagesQuery,
  PackagingItem,
} from "shared/api";
import { adaptPackagesForMultipleExecuteReport } from "shared/adapters";
import { ExecuteReportValues } from "shared/types";
import { Colors } from "shared/themes";
import { FormSelect, Loader } from "shared/ui";
import { selectFiltering } from "shared/helpers";

const CompanyOrdersPackagesFilter: FC = () => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });
  const { values, setFieldValue, handleBlur } =
    useFormikContext<ExecuteReportValues>();

  const companyId = values.company_id;
  const orderIds = values.order_ids ?? [];

  const { data: companies, isLoading: isLoadingCos } = useGetAllCompaniesQuery(
    {}
  );

  const { data: ordersData, isLoading: isLoadingOrders } = useGetAllOrdersQuery(
    { buyer: companyId as number },
    { skip: !companyId }
  );

  const { data: packagings, isLoading: isLoadingPacks } =
    useGetAllPackagesQuery(
      { order__in: orderIds, expand: "material,order" },
      { skip: !orderIds.length }
    );

  const adaptedCompanies = useMemo(
    () => companies?.map((c) => ({ label: c.name, value: c.id })) ?? [],
    [companies]
  );

  const adaptedOrders = useMemo(() => {
    const orders = Array.isArray(ordersData) ? ordersData : ordersData?.results;
    return (
      orders?.map((o) => ({
        label: o.order_code || `ID: ${o.id}`,
        value: o.id,
      })) ?? []
    );
  }, [ordersData]);

  const adaptedPackages = useMemo(
    () =>
      adaptPackagesForMultipleExecuteReport(
        packagings as unknown as PackagingItem[]
      ),
    [packagings]
  );

  useEffect(() => {
    if (companyId) {
      setFieldValue("order_ids", []);
      setFieldValue("packaging_ids", []);
    }
  }, [companyId, setFieldValue]);

  useEffect(() => {
    if (!orderIds.length) {
      setFieldValue("packaging_ids", []);
    }
  }, [orderIds.length, setFieldValue]);

  if (isLoadingCos) return <Loader type="fullscreen-backdrop" />;

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-2 items-center">
          <Title level={5} className="!w-[140px] !m-0">
            {t("selectCompany")}
          </Title>
          {" - "}
          <Select
            showSearch
            size="small"
            className="w-[300px]"
            placeholder={t("selectCompany")}
            value={companyId ?? undefined}
            options={adaptedCompanies}
            onChange={(val) => setFieldValue("company_id", val)}
            filterOption={(input, option) => selectFiltering(input, option)}
          />
        </div>

        {!!companyId && (
          <div className="flex gap-x-2 items-center">
            <Title level={5} className="!w-[140px] !m-0">
              {t("selectOrder")}
            </Title>
            {" - "}
            <Select
              mode="multiple"
              size="small"
              className="w-[450px]"
              loading={isLoadingOrders}
              placeholder={t("selectOrder")}
              value={orderIds}
              options={adaptedOrders}
              onChange={(val) => setFieldValue("order_ids", val)}
              filterOption={(input, option) => selectFiltering(input, option)}
              maxTagCount="responsive"
            />
          </div>
        )}

        {!!orderIds.length && (
          <div className="flex gap-x-2 items-center">
            <Title level={5} className="!w-[140px] !m-0">
              {t("packages")}
            </Title>
            {" - "}

            {isLoadingPacks && <Loader />}

            {!isLoadingPacks && !!adaptedPackages.length && (
              <div className="w-[450px]">
                <FormSelect
                  multiple
                  size="small"
                  fieldName="packaging_ids"
                  placeholder={t("packages")}
                  options={adaptedPackages}
                  value={values.packaging_ids}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                />
              </div>
            )}

            {!isLoadingPacks && !adaptedPackages.length && (
              <Alert
                type="warning"
                message={t("noOptions")}
                className="py-0 px-2"
              />
            )}
          </div>
        )}
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { CompanyOrdersPackagesFilter };
